import { api } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { markPrintingDisabled } from '../hooks/usePrintingAvailability';
import { PRINTING_ERRORS, printingErrorCode } from '../lib/printing-errors';
import type {
  ActivateSetupResponse,
  AgentRelease,
  AgentUpdateMode,
  AgentUpdateRequestResponse,
  CategoryProductionDestination,
  ConnectionTestResponse,
  CategoryRoutesBatchRequest,
  CreateNetworkPrinterRequest,
  DeactivateSetupResponse,
  OrderPrintJobs,
  PairingRequestView,
  PauseStateResponse,
  PrintAgent,
  PrintAgentsResponse,
  Printer,
  PrinterWidth,
  PrintersResponse,
  PrintingAssignments,
  PrintingRoutes,
  PrintingSetup,
  PrintingStatus,
  ProductionDestination,
  ProductionStation,
  ReprintResponse,
  SaveAssignmentsRequest,
  SaveStationRequest,
  StationMoveTarget,
  StationsResponse,
  TestPrintResponse,
  TicketChoice,
  TicketKind,
  UpdatePrinterRequest,
} from '../types/printing.types';

const PRINTING = '/printing';
const ORDERS = '/orders';

/**
 * Toda chamada da impressão passa por aqui: o 404 `printing.disabled` marca a
 * loja como fora do piloto, e a UI de impressão some na hora.
 */
async function gate<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (printingErrorCode(error) === PRINTING_ERRORS.disabled) {
      markPrintingDisabled(useAuthStore.getState().selectedTenant?.id ?? '');
    }
    throw error;
  }
}

async function data<T>(request: Promise<{ data: T }>): Promise<T> {
  const response = await gate(request);
  return response.data;
}

// --- Estado e assistente ---

export const getPrintingStatus = () => data(api.get<PrintingStatus>(`${PRINTING}/status`));

export const getPrintingSetup = () => data(api.get<PrintingSetup>(`${PRINTING}/setup`));

export const activatePrinting = () =>
  data(api.post<ActivateSetupResponse>(`${PRINTING}/setup/activate`));

export const pausePrinting = () => data(api.post<PauseStateResponse>(`${PRINTING}/setup/pause`));

export const resumePrinting = () => data(api.post<PauseStateResponse>(`${PRINTING}/setup/resume`));

export const deactivatePrinting = () =>
  data(api.post<DeactivateSetupResponse>(`${PRINTING}/setup/deactivate`));

// --- Pontos de produção ---

export const getStations = () => data(api.get<StationsResponse>(`${PRINTING}/stations`));

export const createStation = (request: SaveStationRequest) =>
  data(api.post<ProductionStation>(`${PRINTING}/stations`, request));

export const updateStation = (id: string, request: SaveStationRequest) =>
  data(api.put<ProductionStation>(`${PRINTING}/stations/${id}`, request));

export const deleteStation = (id: string, moveTo?: StationMoveTarget) =>
  gate(
    api.delete(`${PRINTING}/stations/${id}`, { params: moveTo ? { moveTo } : undefined }),
  ).then(() => undefined);

// --- Roteamento ---

export const getRoutes = () => data(api.get<PrintingRoutes>(`${PRINTING}/routes`));

export const saveCategoryRoute = (categoryId: string, destination: CategoryProductionDestination) =>
  gate(api.put(`${PRINTING}/routes/categories/${categoryId}`, destination)).then(() => undefined);

export const saveCategoryRoutes = (request: CategoryRoutesBatchRequest) =>
  gate(api.put(`${PRINTING}/routes/categories`, request)).then(() => undefined);

export const saveProductRoute = (productId: string, destination: ProductionDestination) =>
  gate(api.put(`${PRINTING}/routes/products/${productId}`, destination)).then(() => undefined);

// --- Computadores e pareamento ---

export const getAgents = () => data(api.get<PrintAgentsResponse>(`${PRINTING}/agents`));

export const renameAgent = (id: string, name: string) =>
  data(api.patch<PrintAgent>(`${PRINTING}/agents/${id}`, { name }));

export const revokeAgent = (id: string) => data(api.delete<PrintAgent>(`${PRINTING}/agents/${id}`));

/** Aceita `XXXX-XXXX`, em minúsculas ou sem o hífen. */
export const getPairingRequest = (code: string) =>
  data(api.get<PairingRequestView>(`${PRINTING}/pairing/${encodeURIComponent(code)}`));

export const confirmPairing = (code: string, name: string) =>
  data(
    api.post<PrintAgent>(`${PRINTING}/pairing/${encodeURIComponent(code)}/confirm`, {
      name: name.trim() || null,
    }),
  );

// --- Versão e atualização do computador (change print-agent) ---

/** Versão, nível e pedido de atualização de cada computador ativo. Responde no desligamento geral. */
export const getAgentRelease = () => data(api.get<AgentRelease>(`${PRINTING}/agent-release`));

/**
 * Pede a atualização do computador. 201 `created` ou 200 `advanced`/`unchanged`;
 * o `outcome` diz se algo mudou, e o `request.mode` é o modo efetivo.
 */
export const requestAgentUpdate = (agentId: string, mode: AgentUpdateMode) =>
  data(api.post<AgentUpdateRequestResponse>(`${PRINTING}/agents/${agentId}/update`, { mode }));

/**
 * "Testar conexão" da impressora de rede, salva ou não: o computador da loja abre e fecha
 * a conexão sem mandar nenhum byte. A API espera até 6 s; o cliente dá folga além disso.
 */
export const testPrinterConnection = (address: string) =>
  data(
    api.post<ConnectionTestResponse>(`${PRINTING}/connection-test`, { address }, { timeout: 20_000 }),
  );

// --- Impressoras e cupons ---

export const getPrinters = () => data(api.get<PrintersResponse>(`${PRINTING}/printers`));

export const createNetworkPrinter = (request: CreateNetworkPrinterRequest) =>
  data(api.post<Printer>(`${PRINTING}/printers`, request));

export const updatePrinter = (id: string, request: UpdatePrinterRequest) =>
  data(api.put<Printer>(`${PRINTING}/printers/${id}`, request));

export const deletePrinter = (id: string) =>
  gate(api.delete(`${PRINTING}/printers/${id}`)).then(() => undefined);

export const getAssignments = () => data(api.get<PrintingAssignments>(`${PRINTING}/assignments`));

export const saveAssignments = (request: SaveAssignmentsRequest) =>
  data(api.put<PrintingAssignments>(`${PRINTING}/assignments`, request));

export const sendTestPrint = (printerId: string, idempotencyKey: string) =>
  data(
    api.post<TestPrintResponse>(
      `${PRINTING}/test`,
      { printerId },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
  );

/**
 * Prévia do cupom com o pedido de exemplo. Vem como Blob porque a rota exige
 * o token: um `<img src>` direto não levaria o cabeçalho.
 */
export const getTicketPreview = (kind: TicketKind, width: PrinterWidth, stationId?: string | null) =>
  data(
    api.get<Blob>(`${PRINTING}/preview/${kind}.png`, {
      params: { width, ...(stationId ? { stationId } : {}) },
      responseType: 'blob',
    }),
  );

// --- Cupons do pedido ---

export const getOrderPrintJobs = (orderId: string) =>
  data(api.get<OrderPrintJobs>(`${ORDERS}/${orderId}/print-jobs`));

export const reprintOrder = (orderId: string, choice: TicketChoice, idempotencyKey: string) =>
  data(
    api.post<ReprintResponse>(`${ORDERS}/${orderId}/reprint`, choice, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  );

/** "Imprimir pelo navegador": a imagem do cupom de um pedido real, sem criar trabalho. */
export const getOrderTicketImage = (
  orderId: string,
  kind: TicketKind,
  stationId: string | null,
  width: PrinterWidth = 'mm80',
) =>
  data(
    api.get<Blob>(`${ORDERS}/${orderId}/tickets/${kind}.png`, {
      params: { width, ...(stationId ? { stationId } : {}) },
      responseType: 'blob',
    }),
  );
